import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import get from 'lodash/get';
import { Form, Select, Spin } from 'antd/es';
import { InboxOutlined } from '@ant-design/icons/lib';
import TextArea from 'antd/es/input/TextArea';
import Dragger from 'antd/es/upload/Dragger';
import { useForm } from 'antd/es/form/Form';

import { useAuth } from '@hooks/useAuth';
import { useLearningMaterial } from '@hooks/useLearningMaterial';

interface FormPromptAIProps {
  contentId?: string;
}

interface LearningMaterialType {
  label: string;
  value: string;
}

export const supportLearningMaterialType: LearningMaterialType[] = [
  {
    label: 'H5P content',
    value: 'h5p-content',
  },
  {
    label: 'Course Presentation',
    value: 'course-presentation',
  },
  {
    label: 'Multiple Choice',
    value: 'multiple-choice',
  },
  {
    label: 'Drag and Drop',
    value: 'drag-and-drop',
  },
  {
    label: 'Interactive Book',
    value: 'interactive-book',
  },
];

export const FormPromptAI = forwardRef(function FormPromptAI(
  props: FormPromptAIProps,
  ref: any,
) {
  const { t } = useTranslation();
  const { loading, learningMaterialCreation, generateLearningMaterialAction } =
    useLearningMaterial();

  const { auth } = useAuth();
  const [refFormPromptAI] = useForm();

  const [canUpload, setCanUpload] = useState<boolean>(true);

  const handleFinishedSubmittion = useCallback(
    (value: any) => {
      value.file_url = get(
        value,
        'file_url.file.response.file_url',
        value.file_url,
      );

      generateLearningMaterialAction(learningMaterialCreation, value.file_url, {
        id: 'anonymous',
      });
    },
    [auth],
  );

  const handleSubmitForm = useCallback(() => {
    refFormPromptAI.submit();
  }, []);

  const handleUploadFileChange = useCallback(({ file }: { file: any }) => {
    if (file?.response?.file_url) {
      setCanUpload(false);
    }
  }, []);

  useEffect(() => {
    return () => {
      setCanUpload(true);
    };
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      submit() {
        handleSubmitForm();
      },
    }),
    [],
  );

  return (
    <Spin spinning={loading}>
      <Form
        layout='vertical'
        form={refFormPromptAI}
        onFinish={handleFinishedSubmittion}
        initialValues={{
          content_type: supportLearningMaterialType[0].value,
        }}
      >
        <Form.Item
          label={t('material.h5p.contentType')}
          name='content_type'
          rules={[
            { message: t('material.h5p.error.contentType'), required: true },
          ]}
          required
        >
          <Select
            className='!w-full !min-w-full'
            options={supportLearningMaterialType.map(({ label, value }) => {
              const contentTypeId = `material.h5p.contentType.${value}`;
              const contentTypeI18N = t(contentTypeId);
              return {
                value,
                label:
                  contentTypeI18N !== contentTypeId ? contentTypeI18N : label,
              };
            })}
          />
        </Form.Item>
        <Form.Item
          label={t('material.h5p.promptContent')}
          name='prompt_content'
          rules={[
            { message: t('material.h5p.error.promptContent'), required: true },
          ]}
          required
        >
          <TextArea rows={15} />
        </Form.Item>
        <Form.Item label={t('material.h5p.uploadFile')} name='file_url'>
          <Dragger
            method='POST'
            action={process.env.REACT_H5P_HUB_API_URL + '/api/h5p/upload/'}
            data={{
              user_id: auth.user_id,
            }}
            name='file'
            onChange={handleUploadFileChange}
            maxCount={1}
          >
            <p className='ant-upload-drag-icon'>
              <InboxOutlined />
            </p>
            <p className='ant-upload-text'>
              {t('material.h5p.uploadFile.dragndrop')}
            </p>
            <p className='ant-upload-hint'>
              {t('material.h5p.uploadFile.extension')}
            </p>
          </Dragger>
        </Form.Item>
      </Form>
    </Spin>
  );
});
